import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, error, hint, className, id, ...rest }, ref) => {
        const inputId = id || rest.name || crypto.randomUUID();
        return (
            <div>
                {label && (
                    <label
                        htmlFor={inputId}
                        className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1"
                    >
                        {label}
                    </label>
                )}
                <input
                    ref={ref}
                    id={inputId}
                    className={cn(
                        'w-full px-3 py-2 rounded-lg border-2 bg-gray-50 dark:bg-slate-700/50',
                        'text-gray-900 dark:text-white',
                        'transition-colors focus:outline-none',
                        error
                            ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 focus:border-brand-blue focus:bg-white dark:focus:bg-slate-700 focus:ring-2 focus:ring-brand-blue/20',
                        className
                    )}
                    aria-invalid={!!error}
                    aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
                    {...rest}
                />
                {error && (
                    <p id={`${inputId}-error`} className="mt-1 text-xs text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}
                {hint && !error && (
                    <p id={`${inputId}-hint`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {hint}
                    </p>
                )}
            </div>
        );
    }
);
Input.displayName = 'Input';
